import { type ChangeEvent, type DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { login } from '@/lib/api/auth.js';
import { listTickets, uploadTicketPhotosBatch, type TicketListItem } from '@/lib/api/tickets.js';
import { useAuthStore } from '@/stores/auth.js';
import { parseApiError } from '@/lib/api/errors.js';

const PHOTO_MODE_KEY = 'fixflow_photo_mode';

interface CapturedPhoto {
  dataUrl: string;
  caption: string;
  id: string;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Login form ───────────────────────────────────────────────────────────────

function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { user, token } = await login(email, password);
      setAuth(user, token);
      localStorage.setItem(PHOTO_MODE_KEY, String(Date.now()));
      onSuccess();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-2xl">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-2xl font-black text-white">
            F
          </div>
          <h1 className="text-xl font-bold text-slate-950">Repairiz Photo Capture</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to upload photos</p>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {error ? (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>
          ) : null}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Thumbnail grid ───────────────────────────────────────────────────────────

function PhotoThumb({
  photo,
  onRemove,
  onCaptionChange,
}: {
  photo: CapturedPhoto;
  onRemove: () => void;
  onCaptionChange: (caption: string) => void;
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <img
        src={photo.dataUrl}
        alt="Captured"
        className="aspect-square w-full object-cover"
      />
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white shadow"
        aria-label="Remove photo"
      >
        ✕
      </button>
      <input
        type="text"
        placeholder="Caption…"
        value={photo.caption}
        onChange={(e) => onCaptionChange(e.target.value)}
        className="w-full border-t border-slate-200 px-2 py-1 text-xs outline-none focus:border-primary"
      />
    </div>
  );
}

// ── Drop zone ────────────────────────────────────────────────────────────────

function DropZone({ onFiles }: { onFiles: (files: File[]) => void }) {
  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDrag(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
    if (files.length) onFiles(files);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []).filter((f) => f.type.startsWith('image/'));
    if (files.length) onFiles(files);
    e.target.value = '';
  }

  return (
    <div
      className={[
        'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-8 text-center transition-colors',
        drag ? 'border-primary bg-primary/5' : 'border-slate-300 bg-slate-50 hover:border-primary/60',
      ].join(' ')}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      onClick={() => fileRef.current?.click()}
    >
      <span className="text-3xl">📷</span>
      <div>
        <p className="text-sm font-semibold text-slate-700">Tap to take photo or select from gallery</p>
        <p className="mt-0.5 text-xs text-slate-500">Or drag &amp; drop images here</p>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export function PhotoCapturePage() {
  const [searchParams] = useSearchParams();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [authed, setAuthed] = useState(isAuthenticated);

  // URL params
  const urlTicketNumber = searchParams.get('ticket') ?? '';
  const urlDeviceId = searchParams.get('deviceId') ?? undefined;
  const urlType = (searchParams.get('type') ?? 'pre') as 'pre' | 'post';

  // Step state
  const [ticketSearch, setTicketSearch] = useState(urlTicketNumber);
  const [searchResults, setSearchResults] = useState<TicketListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<TicketListItem | null>(null);
  const [photoType, setPhotoType] = useState<'pre' | 'post'>(urlType);
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadedCount, setUploadedCount] = useState<number | null>(null);

  // If URL has ticket number, auto-search on mount
  useEffect(() => {
    if (authed && urlTicketNumber) {
      void performSearch(urlTicketNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  async function performSearch(query: string) {
    if (!query.trim()) return;
    setSearching(true);
    setSearchError('');
    setSearchResults([]);
    setSelectedTicket(null);
    try {
      const res = await listTickets({ search: query.trim(), pageSize: 5 });
      setSearchResults(res.data ?? []);
      if (!res.data?.length) setSearchError('No tickets found.');
    } catch (err) {
      setSearchError(parseApiError(err).message);
    } finally {
      setSearching(false);
    }
  }

  const addFiles = useCallback(async (files: File[]) => {
    const newPhotos: CapturedPhoto[] = [];
    for (const file of files) {
      try {
        const dataUrl = await fileToDataUrl(file);
        newPhotos.push({ dataUrl, caption: '', id: `${Date.now()}-${Math.random()}` });
      } catch {
        // skip unreadable file
      }
    }
    setPhotos((prev) => [...prev, ...newPhotos]);
  }, []);

  async function handleUpload() {
    if (!selectedTicket || photos.length === 0) return;
    setUploading(true);
    setUploadError('');
    try {
      const result = await uploadTicketPhotosBatch(selectedTicket.id, {
        deviceId: urlDeviceId,
        photoType,
        photos: photos.map((p) => ({ dataUrl: p.dataUrl, caption: p.caption || undefined })),
      });
      setUploadedCount(result.uploaded);
      setPhotos([]);
    } catch (err) {
      setUploadError(parseApiError(err).message);
    } finally {
      setUploading(false);
    }
  }

  function handleAddMore() {
    setUploadedCount(null);
  }

  function handleDone() {
    window.close();
    // Fallback if window.close() doesn't work (e.g., not opened as popup)
    setUploadedCount(null);
    setSelectedTicket(null);
    setTicketSearch('');
    setPhotos([]);
  }

  if (!authed) {
    return <LoginForm onSuccess={() => setAuthed(true)} />;
  }

  // ── Success screen ────────────────────────────────────────────────────────
  if (uploadedCount !== null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
        <div className="w-full max-w-sm rounded-2xl bg-white p-8 text-center shadow-2xl">
          <div className="mb-4 text-5xl">✅</div>
          <h2 className="text-xl font-bold text-slate-950">
            {uploadedCount} photo{uploadedCount !== 1 ? 's' : ''} uploaded!
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {photoType === 'pre' ? 'Pre-repair' : 'Post-repair'} photos added to{' '}
            <span className="font-semibold text-slate-900">{selectedTicket?.ticketNumber}</span>
          </p>
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={handleAddMore}
              className="flex-1 rounded-xl border border-slate-300 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Add More
            </button>
            <button
              type="button"
              onClick={handleDone}
              className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-12 pt-6">
      <div className="mx-auto max-w-lg px-4">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-lg font-black text-white">
            F
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Photo Capture</h1>
            <p className="text-xs text-slate-400">Repairiz</p>
          </div>
        </div>

        {/* Step 1: Ticket selection */}
        <section className="mb-4 rounded-2xl bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">
            1. Select Ticket
          </h2>

          {selectedTicket ? (
            <div className="flex items-start justify-between gap-3 rounded-xl border border-green-200 bg-green-50 p-3">
              <div>
                <p className="font-bold text-slate-950">{selectedTicket.ticketNumber}</p>
                <p className="text-xs text-slate-600">
                  {selectedTicket.customerDisplayName ?? selectedTicket.customerNameOverride ?? 'Walk-in'}
                  {selectedTicket.deviceSummary ? ` · ${selectedTicket.deviceSummary}` : ''}
                </p>
                {selectedTicket.repairSummary ? (
                  <p className="mt-0.5 text-xs text-slate-500">{selectedTicket.repairSummary}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => { setSelectedTicket(null); setSearchResults([]); }}
                className="shrink-0 text-xs font-semibold text-slate-500 hover:text-slate-800"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter ticket number…"
                  value={ticketSearch}
                  onChange={(e) => setTicketSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void performSearch(ticketSearch); }}
                  className="flex-1 rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                />
                <button
                  type="button"
                  onClick={() => void performSearch(ticketSearch)}
                  disabled={searching || !ticketSearch.trim()}
                  className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {searching ? '…' : 'Find'}
                </button>
              </div>

              {searchError ? (
                <p className="mt-2 text-sm text-red-600">{searchError}</p>
              ) : null}

              {searchResults.length > 0 ? (
                <ul className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-slate-50">
                  {searchResults.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => { setSelectedTicket(t); setSearchResults([]); }}
                        className="w-full px-3 py-3 text-left hover:bg-primary/5"
                      >
                        <p className="text-sm font-bold text-slate-900">{t.ticketNumber}</p>
                        <p className="text-xs text-slate-500">
                          {t.customerDisplayName ?? t.customerNameOverride ?? 'Walk-in'}
                          {t.deviceSummary ? ` · ${t.deviceSummary}` : ''}
                          {' · '}
                          <span className="capitalize">{t.status.replace(/_/g, ' ')}</span>
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </section>

        {/* Step 2: Photo type */}
        {selectedTicket ? (
          <section className="mb-4 rounded-2xl bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">
              2. Photo Type
            </h2>
            <div className="flex gap-3">
              {(['pre', 'post'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setPhotoType(t)}
                  className={[
                    'flex-1 rounded-xl py-2.5 text-sm font-semibold',
                    photoType === t
                      ? 'bg-primary text-white'
                      : 'border border-slate-300 text-slate-700 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {t === 'pre' ? '🔧 Pre-Repair' : '✅ Post-Repair'}
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {/* Step 3: Camera / drop zone */}
        {selectedTicket ? (
          <section className="mb-4 rounded-2xl bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">
              3. Add Photos
            </h2>
            <DropZone onFiles={(files) => void addFiles(files)} />

            {photos.length > 0 ? (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {photos.map((photo) => (
                  <PhotoThumb
                    key={photo.id}
                    photo={photo}
                    onRemove={() => setPhotos((prev) => prev.filter((p) => p.id !== photo.id))}
                    onCaptionChange={(caption) =>
                      setPhotos((prev) =>
                        prev.map((p) => (p.id === photo.id ? { ...p, caption } : p)),
                      )
                    }
                  />
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        {/* Step 4: Upload */}
        {selectedTicket && photos.length > 0 ? (
          <section className="rounded-2xl bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">
              4. Upload
            </h2>
            {uploadError ? (
              <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{uploadError}</p>
            ) : null}
            <button
              type="button"
              onClick={() => void handleUpload()}
              disabled={uploading}
              className="w-full rounded-xl bg-accent py-3 text-sm font-bold text-white disabled:opacity-50"
            >
              {uploading
                ? 'Uploading…'
                : `Upload ${photos.length} photo${photos.length !== 1 ? 's' : ''} →`}
            </button>
          </section>
        ) : null}
      </div>
    </div>
  );
}
